/**
 * Which courses a category, or a whole standard, applies to.
 *
 * Set on a top-level category in Settings. A category with no entry applies to
 * every course; one with an entry applies only to the courses listed. A
 * course's question editor offers the content categories and the standards
 * that apply to it, and nothing else.
 *
 * Courses are named by their bank course folder id (`course-nurs-210`), the
 * same id the question's location carries.
 */

import { categoryIdsForCourse, courseFolders } from "./course"
import { getDescendantCategoryIds } from "./tree"
import type { ExamCategory } from "./types"

/**
 * Category id (any level) or level key (`bloom:Apply`, `difficulty:easy`) to the
 * course folder ids it applies to. Absent: every course.
 */
export type CourseAssignments = Record<string, string[]>

type FolderLike = { id: string; name: string; parentId: string | null }

/**
 * Where assignments start: each course keeps the subject areas its bank folder
 * already carries, so nothing moves the day the setting appears.
 */
/** The key a Bloom's or Difficulty level is mapped under. */
export const levelMappingKey = (kind: string, value: string) => `${kind}:${value}`

export function seedCourseAssignments(
  folders: readonly FolderLike[],
  categories: readonly ExamCategory[],
): CourseAssignments {
  const assignments: CourseAssignments = {}
  const roots = new Set(categories.filter((category) => category.parentId === null).map((category) => category.id))
  for (const course of courseFolders(folders)) {
    for (const id of categoryIdsForCourse(folders, categories, course.id)) {
      if (!roots.has(id)) continue
      assignments[id] = [...(assignments[id] ?? []), course.id]
    }
  }
  return assignments
}

/** True when this top-level category applies to the course. */
export function appliesToCourse(
  assignments: CourseAssignments,
  rootId: string,
  courseId: string | undefined,
): boolean {
  const courses = assignments[rootId]
  if (!courses) return true
  return courseId ? courses.includes(courseId) : true
}

/**
 * Every category under a top-level category that does not apply to this course.
 * The editor leaves these out of what it offers.
 */
export function excludedForCourse(
  categories: readonly ExamCategory[],
  assignments: CourseAssignments,
  courseId: string | undefined,
): Set<string> {
  const excluded = new Set<string>()
  if (!courseId) return excluded
  // Any category can be limited to some courses, a child as well as a top-level
  // one; what sits under it goes with it.
  for (const category of categories) {
    if (excluded.has(category.id) || appliesToCourse(assignments, category.id, courseId)) continue
    excluded.add(category.id)
    for (const id of getDescendantCategoryIds([...categories], category.id)) excluded.add(id)
  }
  return excluded
}
